import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ViewChild, ChangeDetectorRef, Inject } from '@angular/core';
import { Observable } from 'rxjs';
import { Store, Action } from '@ngrx/store';
import { Actions } from '@ngrx/effects';
import { FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from "@angular/material";

import { ModuleConfiguration } from '../../customer/customer.module.config';
import * as fromStore from '../../customer/store/reducers';
import * as userActions from '../../customer/store/actions';
import * as securityStore from '../store/reducers';
import * as securityActions from '../store/actions';
import * as workspaceActions from '../../pillar/workspace/store/actions';
import * as errorActions from '../../pillar/common/actions/ActionError';
import { AutoSaveFormBuilder } from '../../pillar/workspace/AutoSaveFormBuilder';
import { Subject } from 'rxjs/Subject';
import { AutoSaveFormGroup } from '../../pillar/workspace/AutoSaveFormGroup';
import { LookupModel } from '../../common/models/LookupModel';
import { RoleModel } from '../../security/models/RoleModel';
import { AlertsComponent } from '../../pillar/alerts/components/alerts.component';
import { FormArray } from '@angular/forms/src/model';
import { ActionBase } from '../../core/store/action/ActionBase';
import { GetCurrentUserSubmittingEntitiesAction } from '../../security/store/actions';
import { UserModel } from '../../customer/models/UserModel';
import { UserNameModel } from '../../customer/models/UserNameModel';
import { NotificationModel } from '../../customer/models/NotificationModel';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'brd-my-profile-dialog',
  templateUrl: './my-profile-dialog.component.html',
  styleUrls: ['./my-profile-dialog.component.scss']
})
export class MyProfileDialogComponent implements OnInit, OnDestroy {
  user: UserModel;
  key: string;
  loading: boolean;
  accountActionLoading: boolean;
  errors$: Observable<string[]>;
  assignedRoles$: Observable<RoleModel[]>;
  destroyed$: Subject<boolean> = new Subject<boolean>();
  formArrayInitMethods = new Map<string, () => void>();
  groupLookups$: Observable<LookupModel[]>;
  notificationTypes$: Observable<LookupModel[]>;
  submittingEntities$: Observable<LookupModel[]>;
  profileForm: FormGroup;
  groupsForm: FormGroup;
  notificationsForm: FormGroup;
  rolesForm: FormGroup;
  actionIdentifiers: string[] = [];
  opened: boolean = false;
  notificationsPermission: string = 'CustomerModule.Users.Notifications:Edit';

  @ViewChild(AlertsComponent)
  alerts: AlertsComponent;

  constructor(private store: Store<any>,
    private formBuilder: AutoSaveFormBuilder,
    private actions$: Actions,
    private changeDetectorRef: ChangeDetectorRef,
    public dialogRef: MatDialogRef<MyProfileDialogComponent>) {
  }

  storeDispatch(action: Action) {
    var actionBase = action as ActionBase;
    if (actionBase) {
      this.actionIdentifiers.push(actionBase.identifier);
    }
    this.store.dispatch(action);
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
  }

  ngOnInit() {
    this.loading = false;
    this.errors$ = this.store.select(fromStore.getUserListErrors);
    this.assignedRoles$ = this.store.select(fromStore.getUserAssignedRoles);

    this.store.select(securityStore.getUserContextUser)
      .takeUntil(this.destroyed$)
      .subscribe((v: UserModel) => {
        if (v && v.key && this.loading) {
          this.loading = false;
          this.key = v.key;
          this.user = v;
          if (!this.profileForm) {
            this.initForms();
          }
          this.changeDetectorRef.markForCheck();
          this.store.dispatch(new userActions.GetNotificationTypesAction());
          this.store.dispatch(new GetCurrentUserSubmittingEntitiesAction());
        } else {
          this.loading = true;
          this.storeDispatch(new securityActions.GetCurrentUserProfileAction());
        }
      });


    this.actions$.ofType(errorActions.ACTION_ERROR)
      .takeUntil(this.destroyed$)
      .filter((a: errorActions.ActionError) => this.actionIdentifiers.includes(a.payload.action.identifier))
      .subscribe((a: errorActions.ActionError) => {
        a.payload.errors.forEach(e => this.alerts.addError(e));
      });

    var updateTypes = [
      workspaceActions.updateModelType(ModuleConfiguration.moduleName, UserModel, UserNameModel),
      workspaceActions.updateModelType(ModuleConfiguration.moduleName, UserModel, 'emailAddress'),
      workspaceActions.updateModelType(ModuleConfiguration.moduleName, UserModel, NotificationModel)
    ];
    this.actions$
      .takeUntil(this.destroyed$)
      .filter(a => Boolean(updateTypes.find(t => a.type === workspaceActions.updateModelSuccessTypeByUpdateType(t))))
      .filter((a: workspaceActions.UpdateModelSuccessAction<UserModel, any>) => a.payload.key === this.key)
      .subscribe((a: workspaceActions.UpdateModelSuccessAction<UserModel, any>) => {
        a.payload.control.saveSuccess(a.payload.model);
        if (a.type === workspaceActions.updateModelSuccessType(ModuleConfiguration.moduleName, UserModel, 'groups')) {
          a.payload.control.disable();
        }
        if (a.type === workspaceActions.updateModelSuccessType(ModuleConfiguration.moduleName, UserModel, NotificationModel)) {
          a.payload.control.disable();
        }
      });
    this.actions$
      .takeUntil(this.destroyed$)
      .filter(a => Boolean(updateTypes.find(t => a.type === workspaceActions.updateModelErrorTypeByUpdateType(t))))
      .filter((a: workspaceActions.UpdateModelErrorAction<UserModel, any>) => a.payload.action.payload.key === this.key)
      .subscribe((a: workspaceActions.UpdateModelErrorAction<UserModel, any>) => {
        a.payload.action.payload.control.saveErrors(a.payload.action.payload.model, a.payload.errors);
      });
    var removeNotificationType = workspaceActions.removeModelSuccessType(ModuleConfiguration.moduleName, UserModel, NotificationModel);
    this.actions$.ofType(removeNotificationType)
      .takeUntil(this.destroyed$)
      .map((a: any) => a.payload)
      .filter(payload => payload.key === this.key)
      .subscribe((payload) => {
        payload.control.saveSuccess(payload.model);
        var formArray = (this.notificationsForm.controls['notifications'] as FormArray);
        var index = formArray.controls.indexOf(payload.control);
        formArray.removeAt(index);
      });

    this.notificationTypes$ = this.store.select(fromStore.getUserNotificationTypes);
    this.submittingEntities$ = this.store.select(securityStore.getUserContextSubmittingEntities);
  }

  onHistoryOpened() {
    this.opened = true;
  }

  onHistoryClosed() {
    setTimeout(() => {
      this.opened = false;
    }, 150);
  }

  initForms(): void {
    this.profileForm = this.formBuilder.group({
      key: [''],
      name: this.formBuilder.group({
        firstName: [{ value: null, disabled: true }, Validators.required],
        lastName: [{ value: null, disabled: true }, Validators.required]
      },
        null,
        this.destroyed$,
        (control) => {
          this.storeDispatch(new workspaceActions.UpdateModelAction(ModuleConfiguration.moduleName, UserModel, UserNameModel, { key: this.key, model: control.value, control: control }));
        },
        this.changeDetectorRef),
      emailAddress: this.formBuilder.control({ value: null, disabled: true },
        Validators.email,
        null,
        this.destroyed$,
        (control) => {
          this.storeDispatch(new workspaceActions.UpdateModelAction(ModuleConfiguration.moduleName, UserModel, 'emailAddress', { key: this.key, model: control.value, control: control }));
        },
        this.changeDetectorRef)
    });
    this.notificationsForm = this.formBuilder.group({
      notifications: this.formBuilder.array(this.user.notifications.map(() => this.initNotification(true)))
    });

    this.formArrayInitMethods.set('notifications', () => this.initNotification());

    this.profileForm.patchValue(this.user);
    this.profileForm.markAsPristine();
    this.notificationsForm.patchValue(this.user);
    this.notificationsForm.markAsPristine();
  }

  addFormGroup(form, name) {
    form.controls[name].push(this.formArrayInitMethods.get(name)());
  }

  initNotification(disable?: boolean) {
    return this.formBuilder.group({
      type: [{ value: null, disabled: disable }, Validators.required],
      submittingEntity: [{ value: null, disabled: disable }, Validators.required]
    },
      null,
      this.destroyed$,
      (control) => {
        if (control.controls["type"].value && control.controls["submittingEntity"].value) {
          this.storeDispatch(new workspaceActions.UpdateModelAction(ModuleConfiguration.moduleName, UserModel, NotificationModel, { key: this.key, model: control.value, control: control }));
        }
      },
      this.changeDetectorRef);
  }

  removeNotification(form, control: AutoSaveFormGroup, index) {
    control.initializeRemove();
    this.storeDispatch(new workspaceActions.RemoveModelAction(ModuleConfiguration.moduleName, UserModel, NotificationModel, { key: this.key, model: control.value, control: control }));
  }

  lookupDisplay(lookup?: LookupModel): string | undefined {
    return lookup ? lookup.description : undefined;
  }

  back() {
    this.dialogRef.close();
  }
}



// WEBPACK FOOTER //
// ./src/app/security/components/my-profile-dialog.component.ts